if (!customElements.get('content-filter')) {
  class ContentFilters extends HTMLElement {
    constructor() {
      super();

      this.initialHeightSet = false;
      this.handleResize = debounce(this.resetItemHeight.bind(this), 200);
    }

    connectedCallback() {
      this.filters = gsap.utils.toArray('content-filter', this);
      this.allFilter = this.filters.find(filter => filter.getAttribute('category') === 'all') ?? this.filters[0];
      this.activeFilter = this.allFilter;
      this.itemMinHeight = 0;
      this.categories = [];

      this.group = this.getAttribute('group');
      this.container = document.querySelector(`[data-filter-group="${this.group}"]`);
      this.items = gsap.utils.toArray('[data-filter-item]', this.container);

      callOnLoad(this.setItemHeight.bind(this));

      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
    }

    addCategory(category) {
      this.categories.push(category);
    }

    resetItemHeight() {
      this.itemMinHeight = 0;
      this.container.style.setProperty('--item-height', '');
      this.setItemHeight();
    }

    setItemHeight() {
      if (this.activeFilter !== this.allFilter) {
        this.setFilter(this.allFilter);
      }

      this.itemMinHeight = Math.ceil(
        Math.max(...this.items.map(item => gsap.getProperty(item, 'height')))
      );
      this.container.style.setProperty('--item-height', `${this.itemMinHeight}px`);

      if (this.activeFilter !== this.allFilter) {
        this.setFilter(this.activeFilter);
      }

      if (!this.initialHeightSet) {
        this.initialHeightSet = true;
        this.container.classList.add('is-filterable');
      }
    }

    setFilter(selectedFilter) {
      this.filters.forEach(filter =>
        filter === selectedFilter ? filter.enable() : filter.disable()
      );
    }

    filter(selectedFilter) {
      this.state = Flip.getState(this.items);

      const containerStartHeight = gsap.getProperty(this.container, 'height');

      this.setFilter(selectedFilter);

      const containerEndHeight = gsap.getProperty(this.container, 'height');

      const ease = selectedFilter.category === 'all' ? 'power3.inOut' : 'power2.inOut';
      const stagger = selectedFilter.category === 'all' ? 0.03 : 0.0;
      const flip = Flip.from(this.state, {
        duration: 0.8,
        scale: true,
        ease,
        stagger,
        absoluteOnLeave: true,
        prune: true,
        simple: true,
        onEnter: elements =>
          gsap.fromTo(
            elements,
            { opacity: 0, scale: 0.9 },
            { ease, opacity: 1, scale: 1, duration: 0.6, delay: 0.3 }
          ),
        onLeave: elements => gsap.to(elements, { ease, opacity: 0, scale: 0.9, duration: 0.6 }),
      });

      flip.fromTo(
        this.container,
        {
          height: containerStartHeight,
        },
        {
          height: containerEndHeight,
          clearProps: 'height',
          duration: flip.duration(),
        },
        0
      );

      this.activeFilter = selectedFilter;
    }

    updateItems(items) {
      this.items.forEach(item => (items.includes(item) ? this.show(item) : this.hide(item)));
    }

    hide(item) {
      item.setAttribute('aria-hidden', 'true');
    }

    show(item) {
      item.setAttribute('aria-hidden', 'false');
    }
  }

  customElements.define('content-filters', ContentFilters);

  class ContentFilter extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.onClick.bind(this);
    }

    connectedCallback() {
      this.parent = this.closest('content-filters');
      this.category = this.getAttribute('category');
      this.parent.addCategory(this.category);
      this.items = this.parent.items.filter(item => this.checkItem(item));

      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
    }

    checkItem(item) {
      if (this.category === 'all') return true;

      return item.dataset.categories.split(' ').includes(this.category);
    }

    hasItem(item) {
      return this.items.includes(item);
    }

    isActive() {
      return this.getAttribute('aria-selected') === 'true';
    }

    onClick() {
      if (this.isActive()) return;

      this.parent.filter(this);
    }

    enable() {
      this.setAttribute('aria-selected', true);
      this.parent.updateItems(this.items);
    }

    disable() {
      this.setAttribute('aria-selected', false);
    }

    toggle() {
      this.isActive() ? this.disable() : this.enable();
    }
  }

  customElements.define('content-filter', ContentFilter);
}
